/**
 * Pure geometry for moving the uPlot viewport from one window to the next.
 *
 * Kept free of uPlot and the DOM so the decisions that matter — what the target
 * window is, and whether a move is a *slide* worth animating or a *jump* that
 * should land immediately — are testable directly.
 */

export interface ScaleRange {
  min: number
  max: number
}

export type ScaleRanges = Record<string, ScaleRange>

export const CHART_VIEWPORT_TRANSITION_MS = 260

/** Beyond this zoom factor a tween reads as a lurch rather than a movement. */
const MAX_ANIMATED_ZOOM_FACTOR = 8

/** Relative tolerance for "these two windows are the same window". */
const RANGE_EPSILON = 1e-9

export const X_SCALE_KEY = 'x'

const isFiniteRange = (range: ScaleRange | undefined): range is ScaleRange =>
  !!range && Number.isFinite(range.min) && Number.isFinite(range.max)

export const easeOutCubic = (t: number): number => {
  const clamped = Math.min(1, Math.max(0, t))
  return 1 - Math.pow(1 - clamped, 3)
}

/**
 * Extent of the x values actually plotted. This is what uPlot's own
 * `autoScaleX` would derive, reproduced here so the x window can be applied
 * (and tweened) explicitly rather than as a side effect of `setData`.
 */
export const getValueExtent = (
  values: ReadonlyArray<number | null | undefined> | undefined
): ScaleRange | null => {
  if (!values || values.length === 0) return null

  let min = Number.POSITIVE_INFINITY
  let max = Number.NEGATIVE_INFINITY

  values.forEach((value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return
    min = Math.min(min, value)
    max = Math.max(max, value)
  })

  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null
}

/**
 * The window the chart should end up showing.
 *
 * Explicitly configured ranges (auto y-ranges computed from the loaded points,
 * or ranges the user pinned via the axis controls) win outright. The x window
 * is only derived from the data when nothing has pinned it — which mirrors
 * uPlot's `setData(data, true)` behaviour without handing over control of
 * *when* the move happens.
 */
export const resolveTargetScaleRanges = ({
  xValues,
  explicitRanges,
}: {
  xValues: ReadonlyArray<number | null | undefined> | undefined
  explicitRanges: ScaleRanges
}): ScaleRanges => {
  const target: ScaleRanges = {}

  Object.entries(explicitRanges).forEach(([key, range]) => {
    if (isFiniteRange(range)) {
      target[key] = range
    }
  })

  if (!target[X_SCALE_KEY]) {
    const extent = getValueExtent(xValues)
    if (extent && extent.max > extent.min) {
      target[X_SCALE_KEY] = extent
    }
  }

  return target
}

const rangesAreEquivalent = (left: ScaleRange, right: ScaleRange): boolean => {
  const scale =
    Math.max(Math.abs(left.min), Math.abs(left.max), Math.abs(right.min), 1) *
    RANGE_EPSILON

  return (
    Math.abs(left.min - right.min) <= scale &&
    Math.abs(left.max - right.max) <= scale
  )
}

export const scaleRangesAreEquivalent = (
  from: ScaleRanges,
  to: ScaleRanges
): boolean =>
  Object.entries(to).every(([key, range]) => {
    const previous = from[key]
    return isFiniteRange(previous) && rangesAreEquivalent(previous, range)
  })

/**
 * Whether moving `from` -> `to` should be tweened.
 *
 * Animating is only honest when the two windows describe overlapping views of
 * the same data: sliding a window sideways, or zooming into part of what is
 * already on screen. A jump to a disjoint window (the date picker moving to
 * another month) has nothing to slide *through* — tweening it would spend the
 * whole animation showing an empty plot, which is a worse lie than the pop.
 */
export const shouldAnimateViewportTransition = (
  from: ScaleRanges,
  to: ScaleRanges
): boolean => {
  const fromX = from[X_SCALE_KEY]
  const toX = to[X_SCALE_KEY]

  if (!isFiniteRange(fromX) || !isFiniteRange(toX)) return false

  const fromSpan = fromX.max - fromX.min
  const toSpan = toX.max - toX.min
  if (fromSpan <= 0 || toSpan <= 0) return false

  if (scaleRangesAreEquivalent(from, to)) return false

  const overlap = Math.min(fromX.max, toX.max) - Math.max(fromX.min, toX.min)
  if (overlap <= 0) return false

  const zoomFactor = Math.max(fromSpan / toSpan, toSpan / fromSpan)
  return zoomFactor <= MAX_ANIMATED_ZOOM_FACTOR
}

const lerp = (from: number, to: number, t: number): number =>
  from + (to - from) * t

/**
 * Intermediate ranges at eased progress `t`. Keys absent from `from` land on
 * their target immediately: there is no honest start point to move them from.
 */
export const interpolateScaleRanges = (
  from: ScaleRanges,
  to: ScaleRanges,
  t: number
): ScaleRanges => {
  const eased = easeOutCubic(t)
  const interpolated: ScaleRanges = {}

  Object.entries(to).forEach(([key, target]) => {
    const start = from[key]

    interpolated[key] = isFiniteRange(start)
      ? {
          min: lerp(start.min, target.min, eased),
          max: lerp(start.max, target.max, eased),
        }
      : target
  })

  return interpolated
}

export const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}
